import { HttpClient } from '@angular/common/http';
import { ElementRef, Injectable, inject, signal } from '@angular/core';
import {
  BoxDetection,
  ModelConfig,
  WorkerConfig,
  RegressionResponse,
  BoxesResponse,
} from '@models';
import {
  AppService,
  CanvasService,
  DebugDataService,
  DebugRenderService,
  MlTrackingService,
} from '@services';
import { SceneStore } from '@stores';
import * as tf from '@tensorflow/tfjs';
import '@tensorflow/tfjs-backend-webgl';
import { lastValueFrom } from 'rxjs';

/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
declare const require: any;

@Injectable({
  providedIn: 'root',
})
export class DetectionService {
  private appService = inject(AppService);
  private canvasService = inject(CanvasService);
  private debugDataService = inject(DebugDataService);
  private debugRenderService = inject(DebugRenderService);
  private mlTrackingService = inject(MlTrackingService);
  private sceneStore = inject(SceneStore);
  private http = inject(HttpClient);

  constructor() {
    setInterval(() => {
      this.debugDataService.updatePerformanceData(this.workerPerformances());
    }, 3000);
  }

  private workers = new Map<string, Worker>();
  detectionSources = new Map<string, ElementRef>();
  workerIds = signal<string[]>([]);
  workerPerformances = signal<{
    [key: string]: { cycleTime: number };
  }>({});

  private boxes: {
    [key: string]: Map<string, BoxDetection[]>;
  };
  boxesHistory = new Map<string, Map<string, BoxDetection[][]>>();
  private regressions = new Map<string, Map<string, number>>();
  regressionHistory = new Map<string, Map<string, number[]>>();
  models = new Map<string, ModelConfig>();

  private historySize = new Map<string, number>();

  addWorkers(workers: { [key: string]: WorkerConfig }): void {
    Object.entries(workers).forEach(async (worker) => {
      const workerId = worker[0];
      const workerConfig = worker[1];
      const modelConfig = await lastValueFrom(
        this.http.get<ModelConfig>(
          'web-models/' +
            this.sceneStore.path() +
            '/' +
            workerConfig.model +
            '/config.json',
        ),
      );

      this.models.set(workerId, {
        yolo: modelConfig.yolo,
        objects: modelConfig.objects,
        confidence: workerConfig.confidence,
      });

      this.workers.set(
        workerId,
        modelConfig.yolo === 'v5'
          ? new Worker(new URL('../workers/yolov5.worker', import.meta.url), {
              type: 'module',
            })
          : modelConfig.yolo === 'v8reg'
            ? new Worker(
                new URL('../workers/yolov8reg.worker', import.meta.url),
                {
                  type: 'module',
                },
              )
            : modelConfig.yolo === 'v10'
              ? new Worker(
                  new URL('../workers/yolov10.worker', import.meta.url),
                  {
                    type: 'module',
                  },
                )
              : new Worker(
                  new URL('../workers/yolov11.worker', import.meta.url),
                  {
                    type: 'module',
                  },
                ),
      );
      this.historySize.set(workerId, workerConfig.historySize);
      this.boxes = {
        ...this.boxes,
        [workerId]: new Map<string, BoxDetection[]>(),
      };
      this.workers.get(workerId).onmessage = ({ data }) => {
        switch (data.type) {
          case 'boxesResult': {
            const { detections } = data;
            this.updateBoxes(workerId, detections);
            this.mlTrackingService.track({
              workerId: workerId,
              detections,
            });
            this.debugRenderService.debugRender();
            break;
          }
          case 'regressionResult': {
            const { detections } = data;
            this.updateRegression(workerId, detections);
            this.mlTrackingService.track({
              workerId: workerId,
              detections,
            });
            break;
          }
          case 'performance': {
            this.workerPerformances.set({
              ...this.workerPerformances(),
              [workerId]: { cycleTime: data.resize + data.detection },
            });
            break;
          }
        }
      };
      this.workers.get(workerId)?.postMessage({
        type: 'loadModel',
        payload: {
          path: this.sceneStore.path(),
          model: workerConfig.model,
          numberOfObjects: modelConfig.objects.length,
        },
      });
      this.debugRenderService.createObjectColors(workerId, modelConfig.objects);
    });
    this.workerIds.set([...this.workers.keys()]);
    this.debugRenderService.workers = [...this.workers.keys()];
  }

  terminateWorker(workerId: string): void {
    this.workers.get(workerId).terminate();
  }

  terminateAllworkers(): void {
    this.workers.forEach((entry, workerId) => {
      this.workers.get(workerId).terminate();
    });
  }

  getAllBoxInstances(objectId: string, workerId: string): BoxDetection[] {
    return this.boxes[workerId].get(objectId) ?? [];
  }

  getBox(objectId: string, workerId: string): BoxDetection {
    return (this.boxes[workerId].get(objectId) ?? [])[0];
  }

  getBoxes(objectIds: string[], workerId: string): BoxDetection[] {
    return objectIds.map(
      (objectId) => (this.boxes[workerId].get(objectId) ?? [])[0],
    );
  }

  getRegression(objectId: string, workerId: string): number {
    return this.regressions.get(workerId).get(objectId) ?? 0;
  }

  getAllObjectNames(): string[] {
    return [...this.models.values()]
      .map((model) => model.objects)
      .reduce((acc, object) => [...acc, ...object.values()], [])
      .reduce((acc, result) => [...acc, ...result], []);
  }

  getBoxesHistorySize(workerId: string): number {
    return this.historySize.get(workerId);
  }

  isDetectionConsistentInHistory(
    objectName: string,
    workerId: string,
    numberOfInstances: number,
    expectedSize: number,
  ): boolean {
    const history = this.boxesHistory.get(workerId)?.get(objectName) ?? [];

    if (history.length < numberOfInstances) {
      return false;
    }

    const recentHistory = history.slice(0, numberOfInstances);

    return recentHistory.every((instance) => instance?.length === expectedSize);
  }

  async detect(workerId: string, sourceId?: string) {
    if (
      this.detectionSources.get(sourceId ?? 'insideView').nativeElement
        .readyState >= 2 &&
      !this.appService.trackingMode()
    ) {
      const tensor = tf.browser.fromPixels(
        this.detectionSources.get(sourceId ?? 'insideView').nativeElement,
      );
      const tensorData = tensor.dataSync();
      const tensorJson = {
        shape: tensor.shape,
        dtype: tensor.dtype,
      };

      tensor.dispose();

      this.workers.get(workerId).postMessage({
        type: 'detect',
        payload: {
          confidence: this.models.get(workerId).confidence,
          tensorJson,
          tensorData,
        },
      });
    }
  }

  updateBoxes(workerId: string, detections: BoxesResponse[]): void {
    const detectionLoop = new Map<string, BoxDetection[]>();

    detections.forEach((detection) => {
      const objectName =
        this.models.get(workerId).objects[detection.objectIndex];
      const minX = Math.round(
        detection.minX * this.canvasService.overlay().width,
      );
      const minY = Math.round(
        detection.minY * this.canvasService.overlay().height,
      );
      const maxX = Math.round(
        detection.maxX * this.canvasService.overlay().width,
      );
      const maxY = Math.round(
        detection.maxY * this.canvasService.overlay().height,
      );

      const centerX = Math.round(minX + (maxX - minX) / 2);
      const centerY = Math.round(minY + (maxY - minY) / 2);

      const objectUpdate = {
        center: { x: centerX, y: centerY },
        min: { x: minX, y: minY },
        max: { x: maxX, y: maxY },
        confidence: detection.confidence,
      };

      detectionLoop.set(objectName, [
        ...(detectionLoop.get(objectName) ?? []),
        objectUpdate,
      ]);
    });

    this.boxes = { ...this.boxes, [workerId]: detectionLoop };

    if (this.appService.debugMode()) {
      this.debugRenderService.updateDebugBoxes(workerId, detectionLoop);
    }

    const newObjects = new Map<string, BoxDetection[][]>();
    this.models.get(workerId).objects.forEach((objectName) => {
      const toChange = this.boxesHistory.get(workerId)?.get(objectName) ?? [];
      if (toChange.length >= this.historySize.get(workerId)) {
        toChange.pop();
      }
      toChange.unshift(detectionLoop.get(objectName));
      newObjects.set(objectName, toChange);
    });
    this.boxesHistory.set(workerId, newObjects);
  }

  updateRegression(workerId: string, regressions: RegressionResponse): void {
    const regressionLoop = new Map<string, number>();
    regressions.forEach((regression, index) => {
      const objectName = this.models.get(workerId).objects[index];
      regressionLoop.set(objectName, regression);
    });

    this.regressions = { ...this.regressions, [workerId]: regressionLoop };

    if (this.appService.debugMode()) {
      this.debugRenderService.updateDebugRegressions(workerId, regressionLoop);
    }

    const newObjects = new Map<string, number[]>();
    this.models.get(workerId).objects.forEach((objectName) => {
      const toChange =
        this.regressionHistory.get(workerId)?.get(objectName) ?? [];
      if (toChange.length >= this.historySize.get(workerId)) {
        toChange.pop();
      }
      toChange.unshift(regressionLoop.get(objectName));
      newObjects.set(objectName, toChange);
    });
    this.regressionHistory.set(workerId, newObjects);
  }

  getNumberOfBoxEmptys(
    objectName: string,
    workerId: string,
    numberOfDetectionsToCheck?: number,
    confidence?: number,
  ): number {
    const history = this.boxesHistory.get(workerId)?.get(objectName) ?? [];
    return history
      ? numberOfDetectionsToCheck -
          history
            .slice(
              0,
              numberOfDetectionsToCheck ?? this.historySize.get(workerId),
            )
            .filter((detection) =>
              detection
                ? detection.every((object) =>
                    (object !== undefined || object?.confidence !== null) &&
                    confidence
                      ? object?.confidence > confidence
                      : true,
                  )
                : false,
            ).length
      : null;
  }

  getDistance(
    objectName: string,
    workerId: string,
    confidence?: number,
  ): number {
    const history =
      this.boxesHistory
        .get(workerId)
        ?.get(objectName)
        .filter((detection) =>
          detection
            ? detection.every((object) =>
                (object !== undefined || object?.confidence !== null) &&
                confidence
                  ? object?.confidence > confidence
                  : true,
              )
            : false,
        ) ?? [];
    if (history.length < 2) {
      return 0;
    }

    const [firstDetection] = history[0];
    const [lastDetection] = history[history.length - 1];

    const dx = lastDetection.center.x - firstDetection.center.x;
    const dy = lastDetection.center.y - firstDetection.center.y;

    return Math.sqrt(dx * dx + dy * dy);
  }

  getNumberOfBoxOccurencies(
    objectName: string,
    workerId: string,
    numberOfDetectionsToCheck?: number,
    confidence?: number,
  ): number {
    const history = this.boxesHistory.get(workerId)?.get(objectName) ?? [];
    return history
      ? history
          .slice(0, numberOfDetectionsToCheck ?? this.historySize.get(workerId))
          .filter((detection) =>
            detection
              ? detection.every((object) =>
                  (object !== undefined || object?.confidence !== null) &&
                  confidence
                    ? object?.confidence > confidence
                    : true,
                )
              : false,
          ).length
      : null;
  }

  getSortedBoxesHorizontally(
    objectName: string,
    worker: string,
  ): BoxDetection[] {
    const history = this.boxesHistory.get(worker).get(objectName) ?? [];
    return history.length > 0
      ? [...history][0].sort((a, b) => a.center.x - b.center.x)
      : [];
  }

  getSortedBoxesVertically(objectName: string, worker: string): BoxDetection[] {
    const history = this.boxesHistory.get(worker).get(objectName) ?? [];
    return history.length > 0
      ? [...history][0].sort((a, b) => a.center.y - b.center.y)
      : [];
  }

  getRegressionAverage(
    objectName: string,
    workerId: string,
    numberOfDetectionsToCheck: number,
  ): number {
    const history = this.regressionHistory.get(workerId)?.get(objectName) ?? [];
    return Math.round(
      history
        ? history
            .slice(
              0,
              numberOfDetectionsToCheck ?? this.historySize.get(workerId),
            )
            .reduce((acc, regression) => acc + regression, 0) /
            numberOfDetectionsToCheck
        : null,
    );
  }

  getRegressionTrend(
    objectName: string,
    workerId: string,
    numberOfDetectionsToCheck: number,
    confidenceLevel?: number,
  ): boolean {
    const historySlice = (
      this.regressionHistory.get(workerId)?.get(objectName) ?? []
    ).slice(0, numberOfDetectionsToCheck ?? this.historySize.get(workerId));
    return (
      historySlice.filter((regression) => regression > (confidenceLevel ?? 0.5))
        .length >
      historySlice.length / 2
    );
  }

  debugRender(): void {
    if (this.appService.debugMode()) {
      this.debugRenderService.debugRender();
    }
  }
}
